import { Fragment, useState } from 'react'

import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react'
import {
  HrefLink,
  IconMoon,
  IconProfile,
  IconSignOut,
  IconSun,
  IconXmarkLight,
  LinkButton,
  Radio,
  RadioGroup,
} from '@smartbear/swagger-portal-component-library'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { useFetcher } from 'react-router'
import { z } from 'zod'

import { useApiHubPlan } from '~/portal/hooks/useApiHubPlan'
import darkMode from '~/portal/images/modes/dark-mode.svg'
import lightMode from '~/portal/images/modes/light-mode.svg'
import systemMode from '~/portal/images/modes/system-mode.svg'
import { MODE } from '~/shared/components/DarkMode/constants'
import { useDarkMode } from '~/shared/components/DarkMode/hook'
import Form from '~/shared/components/Form'
import { useEnvironment } from '~/shared/hooks/useEnvironment.ts'
import { Environment } from '~/shared/types/environment'
import { UserInfo } from '~/shared/types/user.ts'

interface ProfileMobileProps {
  title: string
  isOpen: boolean
  close: () => void
  user?: UserInfo,
  accentColor?: string,
  fontFamily?: string,
}

export const ProfileMobile = ({
  title,
  user,
  isOpen,
  close,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for backward-compatible prop signature; login button border is now driven solely by --sp-color-accent
  accentColor,
  fontFamily,
}: ProfileMobileProps) => {
  const { t } = useTranslation()

  const fetcher = useFetcher()

  const { isExternalLoginEnabled } = useApiHubPlan()

  const [isVisible, setIsVisible] = useState(isOpen)

  const { setMode, isDark, isSystem } = useDarkMode()

  const toggleDarkMode = (mode: string) => setMode(mode as MODE)

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(() => close(), 400)
  }

  const env = useEnvironment()

  const showManageAccountLink = !isExternalLoginEnabled && (env !== Environment.ON_PREM || (env === Environment.ON_PREM && user?.authType?.toLowerCase() === 'internal'))

  return (
    <Transition appear show={isVisible} as={Fragment}>
      {/* HeadlessUI always portals Dialog content to a global root outside this component's DOM,
          so the sp-custom-css-scope/sp-app classes are added here to travel with the portaled subtree */}
      <Dialog as="div" className="sp-custom-css-scope sp-app relative z-50" onClose={handleClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-(--sp-color-overlay)" />
        </TransitionChild>
        <div className="fixed inset-y-0 right-0 flex max-w-full">
          <TransitionChild
            as={Fragment}
            enter="transition-transform ease-in-out duration-400"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition-transform ease-in-out duration-400"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <DialogPanel className="sp-profile-mobile w-screen max-w-[400px] bg-(--sp-color-surface)">
              <div className="sp-profile-mobile-header flex items-center justify-between p-6 border-b border-(--sp-color-border-subtle)">
                <DialogTitle className="sp-profile-mobile-title font-medium text-(--sp-color-text) text-h3">
                  {title}
                </DialogTitle>
                <button onClick={handleClose} data-testid="close-button" className="sp-profile-mobile-close p-[0.375rem] text-(--sp-color-text-muted)">
                  <IconXmarkLight height={20} width={20} />
                </button>
              </div>
              {user?.email && <div className="sp-profile-mobile-user flex items-center w-full p-6 border-b border-(--sp-color-border-subtle)">
                <div className="flex-0 text-(--sp-color-text-muted)">
                  <IconProfile height={40} width={40} />
                </div>
                <div className="px-4">
                  <div
                    aria-label={t('components.profile.buttons.email.ariaLabel')}
                    className="sp-profile-mobile-email mb-1 overflow-hidden font-medium text-ellipsis whitespace-nowrap text-body2 text-(--sp-color-text-muted)"
                  >
                    {user.email}
                  </div>
                  {showManageAccountLink &&
                    <HrefLink
                      className="sp-profile-mobile-manage-link font-medium text-(--sp-color-link)"
                      aria-label={t('components.profile.buttons.manageAccount.ariaLabel')}
                      label={t('components.profile.buttons.manageAccount.label')}
                      href={user.profile}
                      target="_blank"
                      isExternal={true}
                    />
                  }
                </div>
              </div>}
              <div className="sp-profile-mobile-mode p-6 border-b border-(--sp-color-border-subtle)">
                <div className="sp-profile-mobile-mode-label flex items-center gap-2 pl-4 mb-5 font-medium text-body2 text-(--sp-color-text)">
                  {isDark() ? <IconMoon /> : <IconSun />}
                  {t('components.ui.modeToggle.label')}
                </div>
                <RadioGroup className="flex flex-col">
                  <Radio
                    value={MODE.LIGHT}
                    checked={!isDark() && !isSystem()}
                    onChange={() => toggleDarkMode(MODE.LIGHT)}
                    image={lightMode}
                    imageClassName="sp-profile-mobile-mode-icon"
                    aria-label={t('components.ui.modeToggle.light')}
                    label={t('components.ui.modeToggle.light')}
                    size="s"
                    className="sp-profile-mobile-mode-radio hover:border-(--sp-color-link) checked:border-(--sp-color-link) checked:hover:border-(--sp-color-link) dark:hover:border-(--sp-color-link) dark:checked:border-(--sp-color-link) dark:checked:hover:border-(--sp-color-link)"
                    innerElementClassName="sp-profile-mobile-mode-radio-dot bg-(--sp-color-link) peer-hover:bg-(--sp-color-link) dark:bg-(--sp-color-link) dark:peer-hover:bg-(--sp-color-link)"
                    containerClassName={clsx(
                      'sp-profile-mobile-mode-item py-2 px-4 border border-transparent',
                      !isDark() && !isSystem() && 'rounded border border-(--sp-color-border) bg-(--sp-color-accent-subtle)',
                    )}
                    labelClassName={clsx(!isDark() && !isSystem() && 'text-(--sp-color-text) dark:text-(--sp-color-text)')}
                    isMobile={true}
                  />
                  <Radio
                    value={MODE.DARK}
                    checked={isDark() && !isSystem()}
                    onChange={() => toggleDarkMode(MODE.DARK)}
                    image={darkMode}
                    imageClassName="sp-profile-mobile-mode-icon"
                    aria-label={t('components.ui.modeToggle.dark')}
                    label={t('components.ui.modeToggle.dark')}
                    size="s"
                    className="sp-profile-mobile-mode-radio hover:border-(--sp-color-link) checked:border-(--sp-color-link) checked:hover:border-(--sp-color-link) dark:hover:border-(--sp-color-link) dark:checked:border-(--sp-color-link) dark:checked:hover:border-(--sp-color-link)"
                    innerElementClassName="sp-profile-mobile-mode-radio-dot bg-(--sp-color-link) peer-hover:bg-(--sp-color-link) dark:bg-(--sp-color-link) dark:peer-hover:bg-(--sp-color-link)"
                    containerClassName={clsx(
                      'sp-profile-mobile-mode-item py-2 px-4 border border-transparent',
                      isDark() && !isSystem() && 'rounded border border-(--sp-color-border) bg-(--sp-color-accent-subtle)',
                    )}
                    labelClassName={clsx(isDark() && !isSystem() && 'text-(--sp-color-text) dark:text-(--sp-color-text)')}
                    isMobile={true}
                  />
                  <Radio
                    value={MODE.SYSTEM}
                    checked={isSystem()}
                    onChange={() => toggleDarkMode(MODE.SYSTEM)}
                    image={systemMode}
                    imageClassName="sp-profile-mobile-mode-icon"
                    aria-label={t('components.ui.modeToggle.system')}
                    label={t('components.ui.modeToggle.system')}
                    size="s"
                    className="sp-profile-mobile-mode-radio hover:border-(--sp-color-link) checked:border-(--sp-color-link) checked:hover:border-(--sp-color-link) dark:hover:border-(--sp-color-link) dark:checked:border-(--sp-color-link) dark:checked:hover:border-(--sp-color-link)"
                    innerElementClassName="sp-profile-mobile-mode-radio-dot bg-(--sp-color-link) peer-hover:bg-(--sp-color-link) dark:bg-(--sp-color-link) dark:peer-hover:bg-(--sp-color-link)"
                    containerClassName={clsx(
                      'sp-profile-mobile-mode-item py-2 px-4 border border-transparent',
                      isSystem() && 'rounded border border-(--sp-color-border) bg-(--sp-color-accent-subtle)',
                    )}
                    labelClassName={clsx(isSystem() && 'text-(--sp-color-text) dark:text-(--sp-color-text)')}
                    isMobile={true}
                  />
                </RadioGroup>
              </div>
              {user?.email ?
                <Form schema={z.object({})} method="get" action="/services/api/logout">
                  {() => (
                    <div
                      aria-label={t('components.profile.buttons.logout.ariaLabel')}
                      className="sp-profile-mobile-logout relative flex items-center w-full p-6 text-(--sp-color-text)"
                    >
                      <input
                        type="submit"
                        name="submit"
                        disabled={fetcher.state === 'submitting' || fetcher.state === 'loading'}
                        className="absolute inset-0 w-full h-full bg-transparent cursor-pointer"
                        value=""
                      />
                      <div className="py-2 pl-4 flex-0">
                        <IconSignOut />
                      </div>
                      <div className="flex-1 pl-2 overflow-hidden font-medium text-body2">
                        {t('components.profile.buttons.logout.label')}
                      </div>
                    </div>
                  )}
                </Form> :
                <div className="p-8">
                  <LinkButton
                    to={`/services/api/login${isExternalLoginEnabled ? '?external=true' : ''}`}
                    type="secondary"
                    data-testid="login-button"
                    size="m"
                    className="w-full justify-center text-(--sp-color-text) border-[1.5px] border-(--sp-color-accent) dark:border-(--sp-color-accent)"
                    style={{ fontFamily }}
                  >
                    {t('components.ui.buttons.logIn.label')}
                  </LinkButton>
                </div>}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}